import Button from 'components/Form/Button';
import Breadcrumb from 'components/Navigation/Breadcrumb';
import React, { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Icon, Icons } from 'components/Icon';
import AssetAvatar from 'components/Avatar/AssetAvatar';
import { useGetTrusteeDetails } from 'common/queries-and-mutations/trustee';
import { Loader } from 'components/Loader';
import { getObjectCategories } from 'utils';
import { AllAsset } from 'constants/dashboard-stats';
import { useAppSelector } from 'store/hooks';
import { selectAccountDetails } from 'selectors/account-selector';
import AssignBank from './TrustAsset/AssignBank';
import AssignStock from './TrustAsset/AssignStock';
import AssignPension from './TrustAsset/AssignPension';
import AssignPhysical from './TrustAsset/AssignPhysical';
import AssignBond from './TrustAsset/AssignBond';
import AssignBenefit from './TrustAsset/AssignBenefit';

export default function AssignTrustee() {
  const [selectedAssetName, setSelectedAssetName] = useState('');

  const { trusteeId } = useParams();
  const { user } = useAppSelector(selectAccountDetails);

  const allowPage = useMemo(() => {
    if (user) {
      return JSON.parse(user.planAllowPage);
    }
  }, [user]);

  const { data, isLoading, isFetching, refetch } = useGetTrusteeDetails({
    trusteeId: Number(trusteeId)
  });

  const renderComponent = useCallback(() => {
    if (selectedAssetName === 'Bank') {
      return (
        <AssignBank
          onSuccess={() => {
            refetch();
            setSelectedAssetName('');
          }}
          trusteeId={Number(trusteeId)}
        />
      );
    }
    if (selectedAssetName === 'Stock') {
      return (
        <AssignStock
          onSuccess={() => {
            refetch();
            setSelectedAssetName('');
          }}
          trusteeId={Number(trusteeId)}
        />
      );
    }
    if (selectedAssetName === 'Pension') {
      return (
        <AssignPension
          onSuccess={() => {
            refetch();
            setSelectedAssetName('');
          }}
          trusteeId={Number(trusteeId)}
        />
      );
    }
    if (selectedAssetName === 'Physical') {
      return (
        <AssignPhysical
          onSuccess={() => {
            refetch();
            setSelectedAssetName('');
          }}
          trusteeId={Number(trusteeId)}
        />
      );
    }
    if (selectedAssetName === 'Bond') {
      return (
        <AssignBond
          onSuccess={() => {
            refetch();
            setSelectedAssetName('');
          }}
          trusteeId={Number(trusteeId)}
        />
      );
    }
    if (selectedAssetName === 'Benefit') {
      return (
        <AssignBenefit
          onSuccess={() => {
            refetch();
            setSelectedAssetName('');
          }}
          trusteeId={Number(trusteeId)}
        />
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAssetName]);

  return (
    <div>
      {isLoading && data && isFetching ? (
        <div className="mt-40 flex flex-col items-center justify-center">
          <Loader color={'#FF6606'} height={50} width={50} />
        </div>
      ) : (
        <>
          <Breadcrumb title="Assets" />
          <div className="mt-5 rounded-lg bg-white">
            <p className="p-8 text-lg  font-medium text-[#353535]">
              Manage Trustee -{' '}
              {`${data?.trusteeFirstName ?? ''} ${
                data?.trusteeMiddleName ?? ''
              } ${data?.trusteeLastName ?? ''}`}
            </p>

            <div className="border-b border-[#E3E3E3] p-5">
              <div className="flex flex-wrap items-center justify-between px-4 pb-1">
                <div className="my-2 mr-3">
                  <div className="inline-flex rounded-xl bg-[#FF66061F] p-0.5 px-3 text-xs  text-[#FF6600]">
                    Trustee{' '}
                    {`${
                      data?.id && (data?.id || 1) > 9
                        ? data?.id
                        : '0' + (data?.id || 0)
                    }`}
                  </div>
                  <div className="mt-5 font-circular text-sm font-medium text-slate-600">
                    Name
                  </div>
                  <div className="font-circular text-base font-medium text-slate-600">
                    {`${data?.trusteeFirstName ?? ''} ${
                      data?.trusteeMiddleName ?? ''
                    } ${data?.trusteeLastName ?? ''}`}
                  </div>
                </div>
                <div className="my-2 mr-3 space-y-2">
                  <div className="inline-flex h-5" />
                  <div className="mt-5 font-circular text-sm font-medium text-slate-600">
                    E-mail address
                  </div>
                  <div className="font-circular text-base font-medium text-slate-600">
                    {data?.emailAddress}
                  </div>
                </div>
                <div className="my-2 mr-3 space-y-2">
                  <div className="inline-flex h-3" />
                  <div className="font-circular text-sm font-medium text-slate-600">
                    Assigned
                  </div>
                  <div className="font-circular text-base font-medium text-slate-600">
                    {data && getObjectCategories(data!).join(', ')}
                  </div>
                </div>
                <div className="my-2 mr-3 space-y-2"></div>
                <div className="my-2 mr-3 space-y-2"></div>
                <div className="mr-3 flex flex-col space-y-2">
                  <div className="inline-flex h-5" />
                  <Link to={`/dashboard/trustee/profile/${trusteeId}`}>
                    <Button
                      variant="outline"
                      size="custom"
                      className="w-[9rem] place-self-end rounded bg-[#FF66061C] p-3 text-center text-xs font-medium text-[#FF6606]"
                      label="Go to profile"
                      icon={<Icon name={Icons.VectorRight} fill="#FF6606" />}
                      iconPosition="right"
                    ></Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="p-11">
              <p className="text-base font-normal text-[#34475E]">
                Select Asset
              </p>
              <div className="mt-8 flex items-center gap-11">
                {AllAsset.filter(stat => allowPage.includes(stat.pageId)).map(
                  asset => {
                    return (
                      <button
                        key={asset.pageId}
                        className="flex flex-col items-center gap-1"
                        onClick={() => {
                          setSelectedAssetName(asset.key);
                        }}
                        type="button"
                      >
                        <AssetAvatar
                          fillIndicator={selectedAssetName === asset.key}
                          image={asset.image}
                        />
                        <p className="mt-2 text-xs text-[#34475E]">
                          {asset.name}
                        </p>
                      </button>
                    );
                  }
                )}
              </div>
              <div>{renderComponent()}</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
